import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { useUserLoggedIn } from '~/modules/user';
import type { Conversation } from '../../types';
import { getChatParticipants } from '../../utils';
import { GroupChatAvatar } from '../avatar/GroupChatAvatar';
import { ThumbnailAvatar } from '../avatar/ThumbnailAvatar';

/**
 * The avatar component for a conversation. This is determined by the conversation
 * type, participants, and thumbnail settings.
 */
export function ConversationAvatar({
    className,
    avatarClassName,
    fallbackClassName,
    conversation,
}: {
    className?: string;
    avatarClassName?: string;
    fallbackClassName?: string;
    conversation: Conversation;
}) {
    const { user, isLoggedIn } = useUserLoggedIn();
    const isDirectConversation =
        conversation.__typename === 'DirectConversation';
    const isGroupChat = isDirectConversation && conversation.IsGroupChat;
    const participants = useMemo(() => {
        return isGroupChat
            ? getChatParticipants(conversation)
            : getChatParticipants(conversation, isLoggedIn ? user.ID : 0);
    }, [isGroupChat, conversation, isLoggedIn, user.ID]);

    return isDirectConversation && conversation.Thumbnail?.Link ? (
        <ThumbnailAvatar
            className={cn('size-12', className)}
            src={conversation.Thumbnail.Link}
        />
    ) : (
        <GroupChatAvatar
            className={cn('size-12', className)}
            avatarClassName={cn('size-12', avatarClassName)}
            fallbackClassName={cn('!text-6', fallbackClassName)}
            participants={participants}
            maxAvatars={isGroupChat ? 7 : 1}
        />
    );
}
